(() => {
  'use strict';
  const instances = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const selectAll = (root, selector) => Array.from(root.querySelectorAll(selector));
  const gsap = window.gsap;
  if (gsap && window.ScrollTrigger) gsap.registerPlugin(window.ScrollTrigger);

  function mount(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const carousels = [];
    const cleanups = [];
    let context;
    const on = (el, event, fn) => el.addEventListener(event, fn, { signal: abort.signal });
    const animate = root.dataset.motion !== 'false' && !reduced.matches;
    if (gsap && animate) {
      context = gsap.context(() => {
        selectAll(root, '[data-reveal]').forEach(el => gsap.from(el, {
          y: 32, autoAlpha: 0, duration: .85, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 95%', once: true },
          clearProps: 'opacity,visibility,transform'
        }));
        selectAll(root, '[data-depth]').forEach(el => gsap.to(el, {
          y: () => root.offsetHeight * Number(el.dataset.depth), ease: 'none',
          scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: 1, invalidateOnRefresh: true }
        }));
      }, root);
    }
    if (window.Splide) selectAll(root, '[data-lv-slider]').forEach(el => {
      const kind = el.dataset.lvSlider;
      const count = el.querySelector('.splide__list')?.children.length || 0;
      if (!count) { el.hidden = true; return; }
      const options = {
        type: ['products', 'promo'].includes(kind) ? 'fade' : 'slide',
        perPage: 1, perMove: 1, gap: '24px', speed: reduced.matches ? 0 : 700,
        rewind: true, pagination: kind === 'promo', arrows: kind !== 'promo' && count > 1,
        drag: count > 1, autoplay: el.dataset.autoplay === 'true' && count > 1 && !reduced.matches,
        interval: 5000, pauseOnHover: true, pauseOnFocus: true,
        reducedMotion: { speed: 0, rewindSpeed: 0, autoplay: 'pause' },
        i18n: { prev: 'Previous slide', next: 'Next slide', play: 'Play slides', pause: 'Pause slides' }
      };
      if (kind === 'gallery') Object.assign(options, {
        perPage: 3, padding: { right: '6%' }, rewind: false, gap: '24px',
        breakpoints: { 1100: { perPage: 2, padding: { right: '12%' } }, 749: { perPage: 1, padding: { right: '18%' }, gap: '16px' } }
      });
      const slider = new Splide(el, options);
      let navigation;
      const navEl = kind === 'products' && root.querySelector('[data-lv-product-nav]');
      if (navEl && count > 1) {
        const nav = new Splide(navEl, {
          perPage: 4, gap: '18px', isNavigation: true, pagination: false, arrows: false,
          speed: reduced.matches ? 0 : 500, drag: count > 4,
          breakpoints: { 749: { perPage: 2, padding: { right: '18%' }, gap: '10px', drag: count > 2 } }
        });
        slider.sync(nav); navigation = nav; carousels.push(nav);
      }
      let transition;
      const animateSlide = () => {
        if (!gsap || !animate || !['products', 'promo'].includes(kind)) return;
        transition?.revert();
        const active = slider.Components.Slides.getAt(slider.index)?.slide;
        if (!active) return;
        transition = gsap.context(() => {
          const copy = active.querySelector('.lv-product-copy, .lv-promo-copy');
          const media = active.querySelector('.lv-product-visual img, .lv-promo-media img');
          if (copy) gsap.fromTo(copy, { y: 20, opacity: .3 }, { y: 0, opacity: 1, duration: .65, clearProps: 'transform,opacity' });
          if (media) gsap.fromTo(media, { scale: 1.045 }, { scale: 1, duration: 1, clearProps: 'transform' });
        }, active);
      };
      slider.on('moved', animateSlide);
      slider.mount(); navigation?.mount(); carousels.push(slider);
      cleanups.push(() => transition?.revert());
      const toggle = el.querySelector('.splide__toggle');
      if (toggle && (!options.autoplay || count <= 1 || reduced.matches)) toggle.hidden = true;
      // Splide supports explicit pause plus hover/focus suspension; also suspend off screen.
      if (options.autoplay) {
        let userPaused = false;
        if (toggle) on(toggle, 'click', () => { userPaused = !toggle.classList.contains('is-active'); });
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting || document.hidden) slider.Components.Autoplay.pause(userPaused);
            else if (!userPaused && !el.matches(':hover') && !el.contains(document.activeElement)) slider.Components.Autoplay.play();
          });
        }, { threshold: .15 });
        observer.observe(el); cleanups.push(() => observer.disconnect());
        on(document, 'visibilitychange', () => {
          if (document.hidden) slider.Components.Autoplay.pause(userPaused);
          else if (!userPaused && el.getBoundingClientRect().bottom > 0 && el.getBoundingClientRect().top < innerHeight && !el.contains(document.activeElement)) slider.Components.Autoplay.play();
        });
      }
      if (window.Shopify?.designMode) on(root, 'shopify:block:select', e => {
        const slide = e.target.closest('.splide__slide');
        const slides = selectAll(el, '.splide__list > .splide__slide');
        if (slide && slides.includes(slide)) { slider.go(slides.indexOf(slide)); slider.Components.Autoplay?.pause(); }
      });
    });

    selectAll(root, '.lv-accordion').forEach(details => {
      const body = details.querySelector('.lv-accordion-body');
      let animation, closingNow = false;
      on(details.querySelector('summary'), 'click', event => {
        if (!animate || !body.animate) return;
        event.preventDefault();
        const currentHeight = body.getBoundingClientRect().height;
        if (animation) { animation.cancel(); animation = null; }
        const closing = details.open && !closingNow;
        closingNow = closing;
        if (!closing) details.open = true;
        animation = body.animate(
          { height: closing ? [currentHeight + 'px', '0px'] : ['0px', body.scrollHeight + 'px'], opacity: closing ? [1, 0] : [0, 1] },
          { duration: 300, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
        animation.onfinish = () => { details.open = !closing; closingNow = false; animation = null; };
      });
      cleanups.push(() => animation?.cancel());
    });

    selectAll(root, '[data-lv-product-form]').forEach(form => on(form, 'submit', async event => {
      // Ordinary form submission remains the fallback if Shopify's cart drawer is unavailable.
      const cart = document.querySelector('cart-drawer');
      if (!cart?.renderContents || !window.routes?.cart_add_url) return;
      event.preventDefault();
      const button = form.querySelector('button');
      const status = form.querySelector('[role="status"]');
      if (button.disabled) return;
      const label = button.textContent;
      button.disabled = true; button.setAttribute('aria-busy', 'true'); button.textContent = 'ADDING…'; status.textContent = '';
      try {
        const data = new FormData(form);
        data.append('sections', cart.getSectionsToRender().map(s => s.id).join(','));
        data.append('sections_url', window.location.pathname);
        cart.setActiveElement(button);
        const response = await fetch(window.routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data, signal: abort.signal });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Unable to add this item. Please try again.');
        cart.classList.remove('is-empty');
        cart.renderContents(result);
        // Keep Dawn's cart page, drawer and quantity listeners synchronized.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lv-featured', productVariantId: data.get('id'), cartData: result });
        status.textContent = 'Added to your bag.';
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = error.message || 'Unable to add this item. Please try again.';
      } finally {
        button.disabled = false; button.removeAttribute('aria-busy'); button.textContent = label;
      }
    }));
    instances.set(root, () => { abort.abort(); cleanups.forEach(fn => fn()); carousels.reverse().forEach(s => s.destroy(true)); context?.revert(); });
  }

  if (!customElements.get('lv-header')) customElements.define('lv-header', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const signal = this.abort.signal;
      let threshold = this.offsetHeight;
      const update = () => {
        if (!this.classList.contains('is-scrolled')) threshold = this.offsetHeight || threshold;
        this.classList.toggle('is-scrolled', window.scrollY > threshold);
      };
      window.addEventListener('scroll', update, { passive: true, signal }); update();
      window.addEventListener('resize', update, { passive: true, signal });
      // Dawn owns #cart-icon-bubble; mirror its cart events onto our own count instead.
      const count = this.querySelector('[data-lv-cart-count]');
      const label = this.querySelector('[data-lv-cart-label]');
      if (count && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          const total = event?.cartData?.item_count;
          if (typeof total !== 'number') return;
          count.textContent = total;
          if (label) label.textContent = total + (total === 1 ? ' item' : ' items');
        });
      }
      selectAll(this, '[data-lv-open]').forEach(button => button.addEventListener('click', () => {
        const dialog = document.getElementById(button.dataset.lvOpen);
        this.returnFocus = button; dialog.showModal(); document.documentElement.style.overflow = 'hidden';
      }, { signal }));
      selectAll(this, 'dialog').forEach(dialog => {
        dialog.addEventListener('keydown', e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); dialog.close(); } }, { signal });
        dialog.addEventListener('close', () => { document.documentElement.style.overflow = ''; this.returnFocus?.focus(); }, { signal });
        dialog.addEventListener('click', e => { if (e.target === dialog) { const r = dialog.getBoundingClientRect(); if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close(); } }, { signal });
        dialog.querySelector('[data-lv-close]')?.addEventListener('click', () => dialog.close(), { signal });
      });
      this.addEventListener('keydown', e => { if (e.key === 'Escape') selectAll(this, '.lv-desktop-menu details[open]').forEach(d => { d.open = false; d.querySelector('summary').focus(); }); }, { signal });
      document.addEventListener('click', e => { if (!this.contains(e.target)) selectAll(this, '.lv-desktop-menu details[open]').forEach(d => d.open = false); }, { signal });
    }
    disconnectedCallback() { this.abort?.abort(); this.unsubscribe?.(); document.documentElement.style.overflow = ''; }
  });
  const boot = (root = document) => selectAll(root, '[data-lv-section]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true }); else boot();
  document.addEventListener('shopify:section:load', e => { boot(e.target); window.ScrollTrigger?.refresh(); });
  document.addEventListener('shopify:section:unload', e => selectAll(e.target, '[data-lv-section]').forEach(root => { instances.get(root)?.(); instances.delete(root); }));
  reduced.addEventListener('change', () => { instances.forEach(destroy => destroy()); instances.clear(); boot(); });
  window.addEventListener('load', () => window.ScrollTrigger?.refresh(), { once: true });
})();
